import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { AgendaResponse, DealDto, EventDto, InvoiceDto, RunningSessionDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { dayLabel, euro, fmtDate, fmtLongDate, fmtTime, localDay, todayKey } from '../format';
import { Empty, ErrorBanner, Panel } from '../ui';

/**
 * Oggi: la prima pagina. A colpo d'occhio cosa c'è in agenda, cosa c'è da fare sui clienti,
 * i soldi da incassare e le novità. Ogni riga porta dove serve con un clic.
 */

const dayKeyOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const eventDay = (e: EventDto) => (e.allDay ? e.startsAt.slice(0, 10) : localDay(e.startsAt));
const dueOf = (i: InvoiceDto): string | null => {
  if (i.dueDate) return i.dueDate;
  if (!i.issueDate) return null;
  const d = new Date(i.issueDate + 'T12:00:00');
  d.setDate(d.getDate() + 30);
  return dayKeyOf(d);
};

function greeting(name: string): string {
  const h = new Date().getHours();
  const g = h < 13 ? 'Buongiorno' : h < 18 ? 'Buon pomeriggio' : 'Buonasera';
  const first = name.split(' ')[0];
  return first && first !== 'Consulente' ? `${g}, ${first}` : g;
}

export function TodayPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const me = ctx.me.user;
  const today = todayKey();
  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return dayKeyOf(d);
  }, []);
  // Stesso filtro dell'Agenda: chi usa il calendario anche per altro (i commerciali) di base vede solo Tecnoside
  const show = localStorage.getItem(`tt.agenda.show.${me.id}`) ?? (me.role === 'agent' ? 'tecnoside' : 'all');

  const [agenda, setAgenda] = useState<AgendaResponse | null>(null);
  const [deals, setDeals] = useState<DealDto[] | null>(null);
  const [open, setOpen] = useState<InvoiceDto[] | null>(null);
  const [running, setRunning] = useState<RunningSessionDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setDate(to.getDate() + 2);
    const safe = async <T,>(p: Promise<T>, fallback: T): Promise<T> => {
      try {
        return await p;
      } catch {
        return fallback;
      }
    };
    try {
      const [a, d, c, r] = await Promise.all([
        api.get<AgendaResponse>(`/api/agenda?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`),
        safe(api.get<DealDto[]>('/api/deals'), []),
        safe(api.get<{ open: InvoiceDto[] }>('/api/collections'), { open: [] }),
        me.role === 'admin' ? safe(api.get<RunningSessionDto | null>('/api/sessions/current'), null) : Promise.resolve(null),
      ]);
      setAgenda(a);
      setDeals(d);
      setOpen(c.open);
      setRunning(r);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, me.role]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const t = ctx.event?.type;
    if (t === 'agenda.changed' || t === 'session.started' || t === 'session.stopped' || t === 'quote.updated') void load();
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCompany = (dealId: number) => {
    ctx.setPage('companies', { company: `d${dealId}`, at: Date.now() });
  };

  // ---- Agenda di oggi e domani ----
  const days = useMemo(() => {
    const evs = (agenda?.events ?? []).filter((e) => !e.done && (show === 'all' || (show === 'together' ? (e.together?.length ?? 0) > 0 : e.tecnoside !== false)));
    const hidden = (agenda?.events ?? []).filter((e) => !e.done).length - evs.length;
    const by = (k: string) => evs.filter((e) => eventDay(e) === k).sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    return { today: by(today), tomorrow: by(tomorrow), hidden, together: evs.filter((e) => (e.together?.length ?? 0) > 0).length };
  }, [agenda, show, today, tomorrow]);

  // ---- Cose da fare sulle trattative ----
  const todo = useMemo(() => {
    const list = (deals ?? []).filter((d) => d.phase !== 'lost' && d.stage !== 'lost' && (d.nextActionText || d.nextActionDate));
    return list.sort((a, b) => (a.nextActionDate ?? '9999').localeCompare(b.nextActionDate ?? '9999') || a.name.localeCompare(b.name, 'it'));
  }, [deals]);
  const late = todo.filter((d) => d.nextActionDate && d.nextActionDate < today).length;

  // ---- Soldi da incassare ----
  const money = useMemo(() => {
    const list = open ?? [];
    const lateList = list.filter((i) => (dueOf(i) ?? '9999') < today);
    return {
      total: list.reduce((n, i) => n + i.totalCents, 0),
      late: lateList.reduce((n, i) => n + i.totalCents, 0),
      lateCount: lateList.length,
      list: list.slice(0, 5),
    };
  }, [open, today]);

  const notices = ctx.notices.slice(0, 6);

  return (
    <div className="td-page">
      <div className="td-hello">
        <div>
          <h1>{greeting(me.name)}</h1>
          <div className="muted">{fmtLongDate(Date.now())}</div>
        </div>
        <div className="td-stats">
          <button type="button" className="td-stat" title="Apri l’agenda" onClick={() => ctx.setPage('agenda')}>
            <b>{days.today.length}</b>
            <span>impegni oggi</span>
          </button>
          <button type="button" className={`td-stat${late ? ' warn' : ''}`} title="Le prossime cose da fare sui clienti: apri le aziende" onClick={() => ctx.setPage('companies')}>
            <b>{todo.length}</b>
            <span>{late ? `da fare, ${late} in ritardo` : 'cose da fare'}</span>
          </button>
          <button type="button" className={`td-stat${money.lateCount ? ' bad' : ''}`} title="Fatture emesse e non ancora pagate: apri gli incassi" onClick={() => ctx.setPage('collections')}>
            <b>{euro(money.total)}</b>
            <span>{money.lateCount ? `da incassare, ${euro(money.late)} in ritardo` : 'da incassare'}</span>
          </button>
        </div>
      </div>
      <ErrorBanner message={error} onClose={() => setError(null)} />

      {running && (
        <button type="button" className="td-running" onClick={() => ctx.setPage('sessions')}>
          <span className="td-pulse" />
          Stai lavorando per <b>{running.clientName}</b> dalle {fmtTime(running.startedAt)}
          <span className="muted"> · apri «Ore di lavoro»</span>
          <Icon name="chevronRight" size={16} />
        </button>
      )}

      <div className="td-grid">
        <Panel
          title="In agenda"
          className="td-card td-blue"
          right={
            <button type="button" className="btn small ghost" onClick={() => ctx.setPage('agenda')}>
              Agenda <Icon name="chevronRight" size={14} />
            </button>
          }
        >
          {!agenda ? (
            <div className="muted small">Caricamento…</div>
          ) : (
            <>
              <DayBlock label="Oggi" events={days.today} deals={(agenda.dealActions ?? []).filter((d) => d.date === today)} onDeal={openCompany} onOpen={() => ctx.setPage('agenda')} />
              <DayBlock label={dayLabel(tomorrow)} events={days.tomorrow} deals={(agenda.dealActions ?? []).filter((d) => d.date === tomorrow)} onDeal={openCompany} onOpen={() => ctx.setPage('agenda')} />
              {days.hidden > 0 && <div className="muted small">{days.hidden === 1 ? '1 altro impegno nascosto' : `${days.hidden} altri impegni nascosti`} (filtro dell'Agenda)</div>}
            </>
          )}
        </Panel>

        <Panel
          title="Da fare sui clienti"
          className="td-card td-violet"
          right={
            <button type="button" className="btn small ghost" onClick={() => ctx.setPage('companies')}>
              Aziende <Icon name="chevronRight" size={14} />
            </button>
          }
        >
          {!deals ? (
            <div className="muted small">Caricamento…</div>
          ) : todo.length === 0 ? (
            <Empty icon="check">Niente in sospeso.</Empty>
          ) : (
            <div className="ag-list">
              {todo.slice(0, 8).map((d) => {
                const isLate = !!d.nextActionDate && d.nextActionDate < today;
                return (
                  <div key={d.id} className={`ag-row td-todo${isLate ? ' late' : ''}`} onClick={() => openCompany(d.id)}>
                    <span className="ag-title">
                      {d.nextActionText || 'Da seguire'}
                      <div className="muted small">
                        {d.name}
                        {d.valueCents ? ` · ${euro(d.valueCents)}` : ''}
                      </div>
                    </span>
                    {d.nextActionDate && <span className={`td-when${isLate ? ' late' : ''}`}>{isLate ? 'in ritardo' : fmtDate(d.nextActionDate)}</span>}
                  </div>
                );
              })}
              {todo.length > 8 && (
                <button type="button" className="ag-hidden" onClick={() => ctx.setPage('companies')}>
                  Altre {todo.length - 8} in Aziende
                </button>
              )}
            </div>
          )}
        </Panel>

        <Panel
          title="Soldi da incassare"
          className="td-card td-green"
          right={
            <button type="button" className="btn small ghost" onClick={() => ctx.setPage('collections')}>
              Incassi <Icon name="chevronRight" size={14} />
            </button>
          }
        >
          {!open ? (
            <div className="muted small">Caricamento…</div>
          ) : open.length === 0 ? (
            <Empty icon="check">Tutto incassato.</Empty>
          ) : (
            <div className="ag-list">
              {money.list.map((i) => {
                const due = dueOf(i);
                const isLate = (due ?? '9999') < today;
                return (
                  <div key={i.id} className="ag-row" onClick={() => ctx.setPage('invoices', { open: i.id })}>
                    <span className="ag-time">{i.number ?? 'Bozza'}</span>
                    <span className="ag-title">
                      {i.clientName ?? i.recipientName}
                      <div className={`small ${isLate ? 'td-late' : 'muted'}`}>{due ? (isLate ? `scaduta il ${fmtDate(due)}` : `scade il ${fmtDate(due)}`) : 'senza scadenza'}</div>
                    </span>
                    <b className="co-amount">{euro(i.totalCents)}</b>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel
          title="Novità"
          className="td-card td-orange"
          right={
            <button type="button" className="btn small ghost" onClick={() => ctx.setPage('notices')}>
              Notifiche <Icon name="chevronRight" size={14} />
            </button>
          }
        >
          {notices.length === 0 ? (
            <Empty icon="bell">Nessuna novità.</Empty>
          ) : (
            <div className="ag-list">
              {notices.map((n) => (
                <div key={n.id} className="ag-row td-notice" onClick={() => ctx.setPage('notices')}>
                  <span className="ag-time">{localDay(n.at) === today ? fmtTime(n.at) : fmtDate(n.at)}</span>
                  <span className="ag-title">
                    {n.title}
                    {n.body && <div className="muted small">{n.body}</div>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

function DayBlock({
  label,
  events,
  deals,
  onDeal,
  onOpen,
}: {
  label: string;
  events: EventDto[];
  deals: AgendaResponse['dealActions'];
  onDeal: (id: number) => void;
  onOpen: () => void;
}) {
  return (
    <div className="td-day">
      <div className="eyebrow-s">{label}</div>
      {events.length === 0 && deals.length === 0 ? (
        <div className="muted small">Niente in programma.</div>
      ) : (
        <div className="ag-list">
          {events.map((e) => {
            const together = (e.together?.length ?? 0) > 0;
            return (
              <div key={e.id} className={`ag-row${together ? ' together' : ''}${e.visitId ? ' visit' : ''}`} onClick={onOpen}>
                <span className="ag-time">{e.allDay ? 'Tutto il giorno' : fmtTime(e.startsAt)}</span>
                <span className="ag-title">
                  {e.title}
                  {e.location && <span className="muted"> · {e.location}</span>}
                </span>
                {together && (
                  <span className="ag-tag together">
                    <Icon name="users" size={12} /> Con {e.together!.join(', ')}
                  </span>
                )}
              </div>
            );
          })}
          {deals.map((d) => (
            <div key={`d${d.dealId}`} className="ag-row deal" onClick={() => onDeal(d.dealId)}>
              <span className="ag-time">Trattativa</span>
              <span className="ag-title">
                {d.text || 'Da seguire'} · <span className="muted">{d.name}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
